import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigateWithOrigin } from '../../../app/nav/originNav'
import { Column } from 'primereact/column'
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog'
import DataTable from '../../../components/ui/DataTable'
import Button from '../../../components/ui/Button'
import { deadlineMinDate, formatDate } from '../../../lib/datetime'
import { deleteLabel } from '../../../lib/deleteLabel'
import { CARD_VIEW_BREAKPOINT, useMediaQuery } from '../../../lib/useMediaQuery'
import type { EditOp } from '../../../lib/editOp'
import { useActionLevels } from '../hooks/useActionLevels'
import { useActionStatuses } from '../hooks/useActionStatuses'
import {
  ACTION_STATUS,
  mainResponsibleOf,
  secondaryResponsiblesOf,
  type ActionFormRow,
  IMPORTANCE_LEVEL,
  URGENCY_LEVEL,
} from '../types'
import { useGrants } from '../../../auth/useCurrentUser'
import { GRANTS } from '../../../auth/grants'
import { useCanFollowActions } from '../hooks/useActionFollow'
import ActionFollowButton from './ActionFollowButton'
import ActionFormDialog from './ActionFormDialog'
import ActionStatusTag from './ActionStatusTag'
import ActionEditorCards from './ActionEditorCards'
import '../actions.css'
import '../../meetings/meetings.css'

interface Props {
  /** Bölüm başlığı (Toplantı Kayıt Tipi'nden — "Aksiyon"). */
  title: string
  value: ActionFormRow[]
  onChange: (rows: ActionFormRow[], op: EditOp) => void
  /** Termin alt sınırı ("YYYY-MM-DD"); toplantı tarihi (analiz 1.10.5: termin toplantıdan önce olamaz). */
  meetingDate?: string
  /** No kolonu gösterilsin mi? Tutanak Oluştur'da kayıt henüz üretilmediğinden numara yok → gizlenir (false). */
  showNumber?: boolean
  /**
   * Satır <b>kaldırma</b> düğmesi çizilsin mi? <b>Varsayılan `false`</b> — açık istek olmadan
   * gelmez.
   *
   * <p><b>Yalnız satırların HENÜZ KALICILAŞMADIĞI ekranda `true` verilir</b> (bugün tek yer:
   * `MinutesCreatePage`). Orada `onChange` saf yerel state'tir (`setActionRows`); satır çıkarmak
   * kaydedilmemiş bir formdan satır çıkarmaktır, **görev silmek değildir** — kayıt ancak
   * "Kaydet (taslak)"/"Yayınla" ile üretilir.
   *
   * <p><b>Toplantı Detayı'nda `false` KALMALIDIR:</b> orada `onChange` her değişikliği anında
   * sunucuya senkronlar (`handleActionsChange` → `syncActions.mutate`), yani satır çıkarmak
   * **kalıcı görevi silmek** demektir — bu, 2026-08-26 kullanıcı kararıyla ("Aksiyonlarda silme
   * özelliği olmayacak") yasaklandı ve `ACTION_DELETE_*` yetkileri DB kataloğundan da
   * kaldırıldı.
   *
   * <p><b>Bu prop neden var:</b> o karar uygulanırken düğme bütün olarak kalkmıştı, çünkü tek bir
   * düğme iki farklı anlamı taşıyordu — "yeni eklediğim satırı geri al" ve "kaydedilmiş görevi
   * sil". Yasak yalnız ikincisini kapsıyordu; birincisi yan hasardı ve yanlışlıkla eklenen bir
   * satır tutanak oluştururken hiç kaldırılamaz hâle gelmişti (kullanıcı isteği 2026-08-30).
   * Prop, iki anlamı ekran bazında ayırır.
   */
  removable?: boolean
  /**
   * **Kaydedilmiş görevi SİL** (2026-09-11, kullanıcı isteği) — `removable`'dan farklıdır ve onun yerine
   * geçmez: `removable` henüz kalıcılaşmamış satırı yerel listeden geri alır (Tutanak Oluştur),
   * bu ise sunucudaki görevi soft-delete eder (alt görevleriyle birlikte, denetim iziyle).
   *
   * <p>Verilmezse çöp kutusu çizilmez. Çağıran bunu yalnız `RECORD_DELETE` yetkisi varken geçer; onay
   * penceresi ve mutasyon orada durur. Yalnız **kaydedilmiş** satırda (id dolu) çizilir — kaydedilmemiş
   * satırın sunucuda karşılığı yoktur, onun yolu `removable`dır.
   */
  onDelete?: (row: ActionFormRow) => void
}

/**
 * Boş yeni satır (yerel key ile). Yeni görevin başlangıç durumu: Görev Bekleniyor (analiz 1.10.6).
 *
 * <p>Aciliyet/Önem **Normal** ile açılır (kullanıcı isteği 2026-08-26): ikisi de zorunlu alan ve en sık
 * seçilen değer Normal. Varsayılan YALNIZ yeni satırda uygulanır — düzenlemede `openEdit` mevcut satırı
 * olduğu gibi taşır, dolayısıyla kayıtlı bir değer üzerine yazılmaz.
 */
function emptyRow(): ActionFormRow {
  return {
    key: crypto.randomUUID(),
    id: null,
    recordNumber: '',
    name: '',
    description: '',
    urgencyLevelId: URGENCY_LEVEL.NORMAL,
    importanceLevelId: IMPORTANCE_LEVEL.NORMAL,
    deadline: '',
    statusId: ACTION_STATUS.PENDING,
    assignees: [],
    // Stratejik Hedef / Katkı (127) — opsiyonel; yeni satır seçimsiz başlar.
    strategicObjectives: [],
    contributions: [],
  }
}

/** Kişi listesini "Ad Soyad, Ad Soyad" olarak birleştirir (tablo gösterimi); boşsa boş dizge. */
function joinNames(people: { fullName: string }[]): string {
  return people.length === 0 ? '' : people.map((p) => p.fullName).join(', ')
}

/**
 * Aksiyon editörü (analiz 1.10) — Tutanak Oluştur/Düzenle içerik bölümü. Eklenen aksiyonlar, <b>Toplantı Detay</b>
 * aksiyon tablosuyla aynı kolonlarda (No · Tanım · Açıklama · Atanan · Termin · Durum) listelenir; ekle/
 * düzenle girişleri karmaşayı önlemek için bir <b>popup</b>'ta toplanır ({@link ActionFormDialog}). <b>Atanan
 * Kişi</b> tablosu henüz yok → görünür-pasif ("—"). Persist Yayınla/Kaydet'te (MEETING_MINUTES).
 */
export default function ActionsEditor({
  title,
  value,
  onChange,
  meetingDate,
  showNumber = true,
  removable = false,
  onDelete,
}: Readonly<Props>) {
  const { t } = useTranslation()
  // 077 — No kolonu yalnız RECORD_NUMBER_VIEW yetkisinde (Özel Kalem) VE showNumber true iken.
  const showRecordNumber = useGrants().hasGrant(GRANTS.RECORD_NUMBER_VIEW)
  // Satırdan Aksiyon Detay'a giderken bu toplantı detayı origin taşınır → "Geri" toplantıya döner.
  const navigate = useNavigateWithOrigin()
  const levels = useActionLevels()
  const statuses = useActionStatuses()
  const statusNameById = useMemo(
    () => new Map((statuses.data ?? []).map((s) => [s.id, s.name])),
    [statuses.data],
  )
  // Termin alt sınırı = max(bugün, toplantı tarihi): geçmiş termin seçilemez; ileri tarihli toplantıda
  // termin toplantıdan önce olamaz (deadlineMinDate güncellendi — geçmiş toplantıda da bugüne çıkar).
  const minDeadline = deadlineMinDate(meetingDate)
  // Dar ekranda (≤CARD_VIEW_BREAKPOINT) tablo yerine kart listesi — bkz. ActionEditorCards.
  const isMobile = useMediaQuery(CARD_VIEW_BREAKPOINT)
  // 120 — takip yıldızı (kaydedilmiş satırlarda; kullanıcı koşulu hook'ta).
  const canFollow = useCanFollowActions()

  // Dialog: editingKey null → yeni ekleme; dolu → o satırı düzenleme.
  const [dialogOpen, setDialogOpen] = useState(false)
  const [draft, setDraft] = useState<ActionFormRow>(emptyRow)
  const [editingKey, setEditingKey] = useState<string | null>(null)

  const openAdd = () => {
    setDraft(emptyRow())
    setEditingKey(null)
    setDialogOpen(true)
  }
  const openEdit = (row: ActionFormRow) => {
    setDraft(row)
    setEditingKey(row.key)
    setDialogOpen(true)
  }
  /*
   * Aksiyon SİLME KALDIRILDI (kullanıcı kararı 2026-08-26): "Aksiyonlarda silme özelliği olmayacak."
   * `ACTION_DELETE_*` yetkileri DB kataloğundan da kaldırıldı; ilgili uç herkese kapalıdır.
   *
   * O karar uygulanırken bu editördeki satır düğmesi BÜTÜN OLARAK kalkmıştı, çünkü tek düğme iki
   * ayrı anlamı taşıyordu: "yeni eklediğim satırı geri al" ve "kaydedilmiş görevi sil". Yasak yalnız
   * ikincisini kapsıyordu; birincisi YAN HASARDI ve tutanak oluştururken yanlışlıkla eklenen bir satır
   * hiç kaldırılamaz hâle gelmişti.
   *
   * 2026-08-30 (kullanıcı isteği) — iki anlam AYRILDI: düğme yalnız `removable` ile, yani satırların
   * henüz kalıcılaşmadığı ekranda çizilir (bugün tek yer: Tutanak Oluştur; orada `onChange` saf yerel
   * state'tir). Toplantı Detayı'nda `removable` verilmez → düğme yoktur, çünkü orada her değişiklik
   * anında sunucuya senkronlanır ve satır çıkarmak gerçekten görev silmek olurdu. Yasak korunur.
   *
   * Arşivlenmiş satırda Düzenle zaten gizlidir (aşağıdaki koşul korunur).
   */
  const removeGroup = 'action-editor-remove' // izole onay grubu (diğer onaylarla çakışmaz)
  const confirmRemove = (row: ActionFormRow) => {
    confirmDialog({
      group: removeGroup,
      header: t('actions.editor.removeConfirm.title'),
      message: t('actions.editor.removeConfirm.message', { name: row.name || '—' }),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: t('actions.editor.remove'),
      accept: () => onChange(value.filter((r) => r.key !== row.key), 'delete'),
    })
  }

  const handleSave = (row: ActionFormRow) => {
    onChange(
      editingKey ? value.map((r) => (r.key === editingKey ? row : r)) : [...value, row],
      editingKey ? 'update' : 'add',
    )
    setDialogOpen(false)
  }

  return (
    <section className="minutes-section">
      <div className="minutes-section-head">
        <h2>{title}</h2>
      </div>

      {value.length === 0 ? (
        <p className="act-empty-hint">{t('actions.editor.empty')}</p>
      ) : isMobile ? (
        <ActionEditorCards
          value={value}
          showRecordNumber={showNumber && showRecordNumber}
          removable={removable}
          canFollow={canFollow}
          statusNameById={statusNameById}
          onOpen={(id) => navigate(`/minutes/actions/${id}`)}
          onEdit={openEdit}
          onRemove={confirmRemove}
          onDelete={onDelete}
        />
      ) : (
        /* Kolonlar Toplantı Detay (ActionsView) ile birebir; sonda editöre özel Düzenle. */
        <DataTable
          value={value}
          dataKey="key"
          paginator={false}
          className="minutes-participant-table"
          onRowClick={(e) => {
            // Kayıtlı aksiyon (id dolu) → Aksiyon Detay'a git. Yeni/kaydedilmemiş satır (id null) → gitme.
            const row = e.data as ActionFormRow
            if (row.id != null) navigate(`/minutes/actions/${row.id}`)
          }}
          rowClassName={(row: ActionFormRow) => (row.id != null ? 'mtg-row-clickable' : '')}
        >
          {showNumber && showRecordNumber && (
            <Column
              header={t('actions.columns.number')}
              body={(row: ActionFormRow) => row.recordNumber || ''}
            />
          )}
          <Column
            field="name"
            header={t('actions.columns.name')}
            body={(row: ActionFormRow) => (
              <span className="minutes-cell-strong act-cell-clip" title={row.name}>
                {row.name || ''}
              </span>
            )}
          />
          <Column
            header={t('actions.form.description')}
            body={(row: ActionFormRow) => (
              <span className="act-cell-clip act-cell-clip--wide" title={row.description}>
                {row.description || ''}
              </span>
            )}
          />
          <Column
            header={t('actions.columns.mainResponsible')}
            body={(row: ActionFormRow) => {
              const name = mainResponsibleOf(row.assignees)?.fullName ?? ''
              return (
                <span className="act-cell-clip" title={name}>
                  {name}
                </span>
              )
            }}
          />
          <Column
            header={t('actions.columns.secondaryResponsibles')}
            body={(row: ActionFormRow) => {
              const names = joinNames(secondaryResponsiblesOf(row.assignees))
              return (
                <span className="act-cell-clip" title={names}>
                  {names}
                </span>
              )
            }}
          />
          <Column
            header={t('actions.columns.deadline')}
            body={(row: ActionFormRow) => (row.deadline ? formatDate(row.deadline) : '')}
          />
          <Column
            header={t('actions.columns.status')}
            body={(row: ActionFormRow) => (
              <ActionStatusTag
                statusId={row.statusId}
                name={row.statusId ? (statusNameById.get(row.statusId) ?? '') : ''}
              />
            )}
          />
          <Column
            header=""
            headerStyle={{ width: '7.5rem' }}
            bodyStyle={{ textAlign: 'center' }}
            body={(row: ActionFormRow) => {
              // Arşivlenmiş aksiyon düzenlenemez → Düzenle butonu gizlenir. Kaldır düğmesi yalnız
              // `removable` ekranlarda (kaydedilmemiş satırlar) çizilir — bkz. yukarıdaki not.
              if (row.statusId === ACTION_STATUS.ARCHIVED) return null
              const removeLabel = deleteLabel(t, 'actions.editor.remove', row.name)
              const deleteActionLabel = deleteLabel(t, 'actions.detail.delete.action', row.name)
              return (
                <div className="act-row-actions">
                  {/* Yıldız (120) — yalnız KAYDEDİLMİŞ satırda: takip sunucudaki göreve bağlanır,
                      kaydedilmemiş satırın (Tutanak Oluştur) karşılığı yoktur. Satır senkronuna
                      (`onChange`) girmez; kendi ucundan değişir. */}
                  {canFollow && row.id != null && (
                    <ActionFollowButton
                      actionId={row.id}
                      name={row.name}
                      followed={row.followed ?? false}
                    />
                  )}
                  <button
                    type="button"
                    className="minutes-remove-btn"
                    aria-label={t('actions.editor.edit')}
                    title={t('actions.editor.edit')}
                    onClick={(ev) => {
                      ev.preventDefault()
                      ev.stopPropagation()
                      openEdit(row)
                    }}
                  >
                    <i className="pi pi-pencil" aria-hidden="true" />
                  </button>
                  {removable && (
                    <button
                      type="button"
                      className="minutes-remove-btn"
                      aria-label={removeLabel}
                      title={removeLabel}
                      onClick={(ev) => {
                        ev.preventDefault()
                        ev.stopPropagation()
                        confirmRemove(row)
                      }}
                    >
                      <i className="pi pi-trash" aria-hidden="true" />
                    </button>
                  )}
                  {/* Sil (2026-09-11) — kaydedilmiş görevi sunucudan siler. `removable` ile aynı anda
                      çizilmez: o ekranda satırlar zaten kalıcı değildir (id null). */}
                  {onDelete && !removable && row.id != null && (
                    <button
                      type="button"
                      className="minutes-remove-btn"
                      aria-label={deleteActionLabel}
                      title={deleteActionLabel}
                      onClick={(ev) => {
                        ev.preventDefault()
                        ev.stopPropagation()
                        onDelete(row)
                      }}
                    >
                      <i className="pi pi-trash" aria-hidden="true" />
                    </button>
                  )}
                </div>
              )
            }}
          />
        </DataTable>
      )}

      <div className="act-add-bar">
        <Button
          type="button"
          variant="secondary"
          icon="pi pi-plus"
          label={t('actions.editor.add')}
          onClick={openAdd}
        />
      </div>


      {removable && <ConfirmDialog group={removeGroup} />}

      <ActionFormDialog
        visible={dialogOpen}
        initial={draft}
        isNew={editingKey === null}
        levels={levels.data}
        minDeadline={minDeadline}
        onSave={handleSave}
        onHide={() => setDialogOpen(false)}
      />
    </section>
  )
}
